/**
 * Proof-of-possession primitives for worker credentials.
 *
 * The bearer token identifies the worker; this ES256 proof demonstrates that
 * the caller also holds the worker's locally-generated private key. The wire
 * format intentionally follows the useful subset of RFC 9449 (DPoP).
 */

export const WORKER_PROOF_HEADER = "dpop";
export const WORKER_PROOF_TYP = "dpop+jwt";
export const WORKER_PROOF_ALG = "ES256";
export const WORKER_PROOF_MAX_AGE_SECONDS = 60;
/** How far ahead of the verifier's clock a proof may be stamped. Machines'
 * clocks wander several seconds between time syncs (measured on a Mac left
 * running: +5 s, -13 s), and a refused proof drops a worker's connection or
 * signs an app out. A proof is still single-use (its jti is consumed) and
 * bound to one method, URL and bearer, so the window replays nothing. */
export const WORKER_PROOF_CLOCK_SKEW_SECONDS = 60;

/** The `code` of a refused worker proof, one per failure class, so a client
 * can tell "sign it again" from "this key is wrong". A `stale` answer also
 * carries `serverTime` (epoch ms) in its body: the client re-signs once, and
 * stamps later proofs on the server's clock. */
export const WorkerProofErrorCode = {
  /** Issued too long ago, or too far ahead of the server's clock. */
  stale: "worker_proof_stale",
  /** Malformed, mis-signed, or bound to another method, URL or token. */
  invalid: "worker_proof_invalid",
  /** No proof header at all (or one past the size limit). */
  missing: "worker_proof_missing",
  /** This proof's one-use id was already consumed. */
  replayed: "worker_proof_replayed",
  /** Signed by a key other than the one bound to the credential. */
  keyMismatch: "worker_proof_key_mismatch",
} as const;
export type WorkerProofErrorCode = (typeof WorkerProofErrorCode)[keyof typeof WorkerProofErrorCode];

/** Which `WorkerProofErrorCode` a `verifyWorkerProof` refusal reason maps to. */
export function workerProofErrorCode(reason: string): WorkerProofErrorCode {
  switch (reason) {
    case "missing_or_oversize": return WorkerProofErrorCode.missing;
    case "stale": return WorkerProofErrorCode.stale;
    case "key_mismatch": return WorkerProofErrorCode.keyMismatch;
    default: return WorkerProofErrorCode.invalid;
  }
}

export interface WorkerPublicJwk {
  kty: "EC";
  crv: "P-256";
  x: string;
  y: string;
}

export interface WorkerProofClaims {
  htm: string;
  htu: string;
  iat: number;
  jti: string;
  ath: string;
}

export interface WorkerProofProvider {
  createProof(input: { method: string; url: string; accessToken: string; now?: number }): Promise<string>;
}

export type WorkerProofVerification =
  | { ok: true; thumbprint: string; jti: string; iat: number }
  | { ok: false; reason: string };

function encodeBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("invalid base64url");
  const standard = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(standard + "=".repeat((4 - standard.length % 4) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function encodeJson(value: unknown): string {
  return encodeBase64Url(new TextEncoder().encode(JSON.stringify(value)));
}

function decodeJson(value: string): unknown {
  return JSON.parse(new TextDecoder().decode(decodeBase64Url(value)));
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return encodeBase64Url(new Uint8Array(digest));
}

/** RFC 7638 canonical member set for a P-256 public key. */
export async function workerKeyThumbprint(jwk: WorkerPublicJwk): Promise<string> {
  if (!isWorkerPublicJwk(jwk)) throw new Error("Invalid worker public key");
  return sha256Base64Url(JSON.stringify({ crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y }));
}

export async function workerAccessTokenHash(token: string): Promise<string> {
  return sha256Base64Url(token);
}

/**
 * Canonical request URI including a deterministically sorted query. Rivets
 * includes the query because several worker endpoints use it semantically;
 * sorting decoded key/value pairs prevents equivalent ordering or percent-
 * encoding choices from causing false mismatches.
 */
export function canonicalWorkerProofUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:" && url.protocol !== "wss:" && url.protocol !== "ws:") {
      return null;
    }
    // A WebSocket begins as an HTTP upgrade. Clients naturally hold a wss://
    // URL while the edge verifies an https:// Request, so canonicalize both to
    // the underlying handshake scheme.
    if (url.protocol === "wss:") url.protocol = "https:";
    if (url.protocol === "ws:") url.protocol = "http:";
    url.hash = "";
    const entries: Array<[string, string]> = [];
    url.searchParams.forEach((value, key) => entries.push([key, value]));
    entries.sort(([leftKey, leftValue], [rightKey, rightValue]) => {
      if (leftKey !== rightKey) return leftKey < rightKey ? -1 : 1;
      if (leftValue === rightValue) return 0;
      return leftValue < rightValue ? -1 : 1;
    });
    url.search = "";
    for (const [key, value] of entries) url.searchParams.append(key, value);
    return url.toString();
  } catch {
    return null;
  }
}

export function isWorkerPublicJwk(value: unknown): value is WorkerPublicJwk {
  if (!value || typeof value !== "object") return false;
  const key = value as Record<string, unknown>;
  if (key.kty !== "EC" || key.crv !== "P-256" || typeof key.x !== "string" || typeof key.y !== "string") return false;
  if ("d" in key) return false;
  try {
    return decodeBase64Url(key.x).byteLength === 32 && decodeBase64Url(key.y).byteLength === 32;
  } catch {
    return false;
  }
}

export function publicWorkerJwk(jwk: JsonWebKey): WorkerPublicJwk {
  const candidate = { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y };
  if (!isWorkerPublicJwk(candidate)) throw new Error("Invalid P-256 worker public key");
  return candidate;
}

export async function createWorkerProof(input: {
  privateKey: CryptoKey;
  publicKey: WorkerPublicJwk;
  method: string;
  url: string;
  accessToken: string;
  now?: number;
  jti?: string;
}): Promise<string> {
  const htu = canonicalWorkerProofUrl(input.url);
  if (!htu) throw new Error("Worker proof URL must be HTTP(S) or WS(S)");
  const random = new Uint8Array(16);
  crypto.getRandomValues(random);
  const header = encodeJson({ typ: WORKER_PROOF_TYP, alg: WORKER_PROOF_ALG, jwk: input.publicKey });
  const payload = encodeJson({
    htm: input.method.toUpperCase(),
    htu,
    iat: Math.floor((input.now ?? Date.now()) / 1_000),
    jti: input.jti ?? encodeBase64Url(random),
    ath: await workerAccessTokenHash(input.accessToken),
  } satisfies WorkerProofClaims);
  const signingInput = `${header}.${payload}`;
  const signature = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    input.privateKey,
    new TextEncoder().encode(signingInput),
  );
  return `${signingInput}.${encodeBase64Url(new Uint8Array(signature))}`;
}

export async function verifyWorkerProof(input: {
  proof: string | null | undefined;
  method: string;
  url: string;
  accessToken: string;
  expectedPublicKey?: WorkerPublicJwk;
  now?: number;
  maxAgeSeconds?: number;
  clockSkewSeconds?: number;
}): Promise<WorkerProofVerification> {
  if (!input.proof || input.proof.length > 4096) return { ok: false, reason: "missing_or_oversize" };
  const parts = input.proof.split(".");
  if (parts.length !== 3 || parts.some((part) => part.length === 0)) return { ok: false, reason: "malformed" };

  try {
    const header = decodeJson(parts[0]!) as Record<string, unknown>;
    const claims = decodeJson(parts[1]!) as Record<string, unknown>;
    if (header.typ !== WORKER_PROOF_TYP || header.alg !== WORKER_PROOF_ALG || !isWorkerPublicJwk(header.jwk)) {
      return { ok: false, reason: "invalid_header" };
    }
    const proofKey = header.jwk;
    const thumbprint = await workerKeyThumbprint(proofKey);
    if (input.expectedPublicKey && thumbprint !== await workerKeyThumbprint(input.expectedPublicKey)) {
      return { ok: false, reason: "key_mismatch" };
    }
    if (typeof claims.htm !== "string" || claims.htm.toUpperCase() !== input.method.toUpperCase()) {
      return { ok: false, reason: "method_mismatch" };
    }
    const target = canonicalWorkerProofUrl(input.url);
    if (!target || claims.htu !== target) return { ok: false, reason: "url_mismatch" };
    if (typeof claims.ath !== "string" || claims.ath !== await workerAccessTokenHash(input.accessToken)) {
      return { ok: false, reason: "token_mismatch" };
    }
    if (typeof claims.iat !== "number" || !Number.isSafeInteger(claims.iat)) {
      return { ok: false, reason: "invalid_iat" };
    }
    if (typeof claims.jti !== "string" || !/^[A-Za-z0-9_-]{16,128}$/.test(claims.jti)) {
      return { ok: false, reason: "invalid_jti" };
    }
    const now = Math.floor((input.now ?? Date.now()) / 1_000);
    const age = input.maxAgeSeconds ?? WORKER_PROOF_MAX_AGE_SECONDS;
    const skew = input.clockSkewSeconds ?? WORKER_PROOF_CLOCK_SKEW_SECONDS;
    if (claims.iat > now + skew || now - claims.iat > age) return { ok: false, reason: "stale" };

    const key = await crypto.subtle.importKey(
      "jwk",
      proofKey,
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    const valid = await crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      key,
      decodeBase64Url(parts[2]!),
      new TextEncoder().encode(`${parts[0]}.${parts[1]}`),
    );
    return valid
      ? { ok: true, thumbprint, jti: claims.jti, iat: claims.iat }
      : { ok: false, reason: "signature" };
  } catch {
    return { ok: false, reason: "malformed" };
  }
}
